import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Globe, 
  Check, 
  ArrowRight, 
  Loader2,
  CreditCard,
  Shield,
  X,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderPage = () => {
  const { websiteId } = useParams();
  const [searchParams] = useSearchParams();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'checking', 'success', 'cancelled', 'failed'
  const [planType, setPlanType] = useState('monthly');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Check for session_id in URL (return from Stripe)
  const sessionId = searchParams.get('session_id');
  const status = searchParams.get('status');

  // Fetch website info
  useEffect(() => {
    const fetchWebsite = async () => {
      if (!websiteId) return;
      try {
        const response = await axios.get(`${API}/websites/preview/${websiteId}`);
        setWebsite(response.data);
        // Check if already ordered
        if (response.data.status === 'ordered') {
          setPaymentStatus('already_ordered');
        }
      } catch (err) {
        toast.error('Website not found');
      } finally {
        setLoading(false);
      }
    };

    fetchWebsite();
  }, [websiteId]);

  // Poll payment status if session_id present
  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('failed');
      toast.error('Payment status check timed out. Please contact support.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sid}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success('Payment successful! Your website is now ordered.');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('failed');
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // If still pending, continue polling
      setPaymentStatus('checking');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('failed');
      toast.error('Error checking payment status.');
    }
  }, []);

  // Handle return from Stripe
  useEffect(() => {
    if (sessionId && status === 'success') {
      setPaymentStatus('checking');
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      setPaymentStatus('cancelled');
    }
  }, [sessionId, status, pollPaymentStatus]);

  const handleCheckout = async () => {
    if (!website?.id) return;
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/payments/checkout/create`, {
        website_id: website.id,
        plan_type: planType,
        origin_url: window.location.origin
      });

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageLayout>
    );
  }

  // Already Ordered View
  if (paymentStatus === 'already_ordered') {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center px-4 pt-16 pb-16">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Already Ordered!</h1>
            <p className="text-muted-foreground mb-6">
              Great news - this website has already been ordered! We're working on getting it live for you.
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              If you have any questions, please contact us at <a href="mailto:andrew@tryweb.co.uk" className="text-primary hover:underline">andrew@tryweb.co.uk</a>
            </p>
            <Link to="/">
              <Button className="gap-2">
                Back to TryWeb <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Payment Success View
  if (paymentStatus === 'success') {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Payment Successful!</h1>
            <p className="text-muted-foreground mb-6">
              Thank you for your order! Your website is now being prepared and will be live soon.
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              You'll receive a confirmation email shortly with all the details.
            </p>
            <Link to="/">
              <Button className="gap-2">
                Back to TryWeb <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Payment Cancelled View
  if (paymentStatus === 'cancelled') {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-orange-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Payment Cancelled</h1>
            <p className="text-muted-foreground mb-6">
              Your payment was cancelled. No worries - you can try again whenever you're ready.
            </p>
            <Button onClick={() => setPaymentStatus(null)} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Checking Payment Status View
  if (paymentStatus === 'checking') {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="max-w-md text-center">
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-4">Verifying Payment...</h1>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment. This may take a few moments.
            </p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">
              Complete Your Order
            </h1>
            <p className="text-muted-foreground">
              You're ordering a website for <strong>{website?.business_name}</strong>
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Select Your Plan - Left side */}
            <div className="bg-card/50 border border-white/5 rounded-2xl p-8 h-fit">
              <h2 className="text-xl font-bold mb-6">Select Your Plan</h2>
              
              <RadioGroup
                value={planType}
                onValueChange={setPlanType}
                className="space-y-4"
              >
                {/* Monthly Subscription */}
                <div 
                  className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer ${
                    planType === 'monthly' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-white/10 hover:border-primary/30'
                  }`}
                  onClick={() => setPlanType('monthly')}
                >
                  <RadioGroupItem value="monthly" id="monthly" data-testid="plan-monthly" className="mt-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="monthly" className="font-semibold cursor-pointer text-lg">
                        Monthly Subscription
                      </Label>
                      <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">Popular</span>
                    </div>
                    <p className="text-2xl font-bold text-primary mt-2">
                      £59.99<span className="text-sm font-normal text-muted-foreground">/month</span>
                    </p>
                  <p className="text-sm text-muted-foreground mt-1">
                      Minimum 12 month term, hosting & support included
                    </p>
                  </div>
                </div>

                {/* Setup + Hosting */}
                <div 
                  className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer ${
                    planType === 'setup_hosting' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-white/10 hover:border-primary/30'
                  }`}
                  onClick={() => setPlanType('setup_hosting')}
                >
                  <RadioGroupItem value="setup_hosting" id="setup_hosting" data-testid="plan-setup-hosting" className="mt-0" />
                  <div className="flex-1">
                    <Label htmlFor="setup_hosting" className="font-semibold cursor-pointer text-lg">
                      Setup + Hosting
                    </Label>
                    <p className="text-2xl font-bold text-primary mt-2">
                      £500 <span className="text-sm font-normal text-muted-foreground">+ £24.99/month</span>
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      One-time setup fee, lower monthly hosting cost
                    </p>
                  </div>
                </div>

                {/* Full Purchase */}
                <div 
                  className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer ${
                    planType === 'full_purchase' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-white/10 hover:border-primary/30'
                  }`}
                  onClick={() => setPlanType('full_purchase')}
                >
                  <RadioGroupItem value="full_purchase" id="full_purchase" data-testid="plan-full-purchase" className="mt-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="full_purchase" className="font-semibold cursor-pointer text-lg">
                        Full Purchase
                      </Label>
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Best Value</span>
                    </div>
                    <p className="text-2xl font-bold text-primary mt-2">
                      £1,399.99
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Own it forever, no monthly fees, full ownership
                    </p>
                  </div>
                </div>
              </RadioGroup>

              <p className="text-sm text-muted-foreground text-center mt-4">
                Select a plan and review your order summary on the right
              </p>
            </div>

            {/* Order Summary - Right side with payment */}
            <div className="space-y-6">
              <div className="bg-card/50 border border-white/5 rounded-2xl p-8">
                <h2 className="text-xl font-bold mb-6">Order Summary</h2>
                
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Website for</span>
                    <span className="font-semibold">{website?.business_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Plan</span>
                    <span className="font-semibold">
                      {planType === 'monthly' && 'Monthly Subscription'}
                      {planType === 'setup_hosting' && 'Setup + Hosting'}
                      {planType === 'full_purchase' && 'Full Purchase'}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground border-t border-white/10 pt-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-secondary" />
                    Custom designed website
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-secondary" />
                    Mobile responsive design
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-secondary" />
                    SEO optimization included
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-secondary" />
                    SSL certificate & hosting
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-secondary" />
                    Free premium domain included
                  </div>
                  {planType === 'full_purchase' && (
                    <>
                      <div className="flex items-center gap-2 text-green-400">
                        <Check className="w-4 h-4" />
                        Full source code ownership
                      </div>
                      <div className="flex items-center gap-2 text-green-400">
                        <Check className="w-4 h-4" />
                        Host anywhere you choose
                      </div>
                    </>
                  )}
                </div>
                
                <div className="border-t border-white/10 pt-4 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-lg">Total Due Now</span>
                    <span className="font-bold text-primary text-2xl">
                      {planType === 'monthly' && '£59.99'}
                      {planType === 'setup_hosting' && '£500'}
                      {planType === 'full_purchase' && '£1,399.99'}
                    </span>
                  </div>
                  {planType === 'monthly' && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Billed monthly, minimum 12 month term
                    </p>
                  )}
                  {planType === 'setup_hosting' && (
                    <p className="text-sm text-muted-foreground mt-1">
                      + £24.99/month hosting after
                    </p>
                  )}
                </div>

                {/* Terms Agreement */}
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-white/10 bg-background/50 mb-4">
                  <Checkbox 
                    id="terms" 
                    checked={agreedToTerms}
                    onCheckedChange={setAgreedToTerms}
                    data-testid="terms-checkbox"
                  />
                  <Label htmlFor="terms" className="text-sm cursor-pointer">
                    I agree to the{' '}
                    <Link to="/terms" className="text-primary hover:underline" target="_blank">
                      Terms of Sale
                    </Link>
                    {planType === 'monthly' && (
                      <span className="text-muted-foreground"> (includes 12 month minimum term)</span>
                    )}
                  </Label>
                </div>

                <Button
                  onClick={handleCheckout}
                  className="w-full glow-primary gap-2"
                  size="lg"
                  disabled={submitting || !agreedToTerms}
                  data-testid="checkout-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Redirecting to Payment...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Proceed to Payment
                    </>
                  )}
                </Button>

                {!agreedToTerms && (
                  <p className="text-xs text-center text-orange-400 mt-2">
                    Please agree to the terms and conditions to proceed
                  </p>
                )}

                <div className="flex items-center justify-center gap-2 mt-4 text-sm text-muted-foreground">
                  <Shield className="w-4 h-4" />
                  <span>Secure payment via Stripe</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default OrderPage;
